import React, { useEffect, useState } from 'react'
import DialogWrapper from '../../../components/core-ui/DialogWrapper'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { workflowService } from '../../../services/admin/workflow'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { adminTeamsService } from '../../../services/admin/teams'
import { GroupHeader, GroupItems, textFieldStyle } from './CreateProcessTemplateDialog'
import { ACTIVITY_TYPES, FIELD_TYPES } from '../../../utility/constants'

const UpdateActivityDetails = ({
  ftData,
  activityTemplate,
  saveActivityDetails = () => {},
  deleteActivity = () => {},
  setDialogOpen = () => {},
}) => {
  const { user } = useAuthContext()
  const [updatedActivity, setUpdatedActivity] = useState(activityTemplate)
  const [allActivities, setAllActivities] = useState([])
  const [teams, setTeams] = useState([])
  const [selectedAssignmentField, setSelectedAssignmentField] = useState(null)
  const [autoCloseEnabled, setAutoCloseEnabled] = useState(!!activityTemplate?.time_in_min)

  useEffect(() => {
    setSelectedAssignmentField(
      ftData.find((it) => it.id === activityTemplate.dynamic_assignment_field_template_id),
    )
  }, [activityTemplate?.dynamic_assignment_field_template_id, ftData])

  const [routes, setRoutes] = useState([])

  useEffect(() => {
    ;(async () => {
      const activities = await workflowService.getActivities(
        user,
        activityTemplate?.process_template_id,
      )
      setAllActivities(activities.filter((it) => it.id != activityTemplate.id))

      const allRoutes = await workflowService.getRoutes(user, activityTemplate?.process_template_id)
      setRoutes(allRoutes.filter((r) => r.from_activity_template_id === activityTemplate.id))

      const teams = await adminTeamsService.getTeams(user)
      setTeams(teams)
    })()
  }, [])

  const getSelectedActivity = (id) => {
    const activity = allActivities.find((it) => it.id == id)
    if (activity) {
      return { label: activity?.activity_template_name, value: activity.id }
    } else {
      return null
    }
  }

  const getRelatedTableName = (tableFieldTemplateId) => {
    const relatedTable = ftData.find((item) => item.id === tableFieldTemplateId)

    return relatedTable ? relatedTable.field_template_name : 'Unknown Table'
  }

  return (
    <DialogWrapper
      fullWidth
      open={true}
      onClose={() => setDialogOpen(false)}
      heading={`${activityTemplate?.activity_template_name} (Activity Settings)`}
      actions={null}
    >
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '.8rem',
          width: '100%',
        }}
      >
        {/* Activity Name */}
        <TextField
          sx={textFieldStyle}
          type="text"
          value={updatedActivity?.activity_template_name}
          fullWidth
          size="small"
          variant="outlined"
          color="primary"
          onChange={(e) => {
            setUpdatedActivity({
              ...updatedActivity,
              activity_template_name: e.target.value,
            })
          }}
          label={`Activity Template Name`}
        />

        {/* Assign to Initiator */}
        <FormControl variant="outlined" fullWidth size="small" color="primary">
          <InputLabel id="select-option-label">Activity Type</InputLabel>
          <Select
            disabled
            size="small"
            color="primary"
            labelId="select-option-label"
            id="select-option"
            value={updatedActivity?.activity_type}
            label="Activity Type"
          >
            {Object.entries(ACTIVITY_TYPES).map(([key, value]) => {
              return <MenuItem value={value}>{value}</MenuItem>
            })}
          </Select>
        </FormControl>

        {/* Description*/}
        <TextField
          sx={textFieldStyle}
          type="text"
          value={updatedActivity?.description}
          fullWidth
          size="small"
          variant="outlined"
          color="primary"
          onChange={(e) => {
            setUpdatedActivity({
              ...updatedActivity,
              description: e.target.value,
            })
          }}
          label={`Activity Description`}
        />

        {/* Associated data entry activity template */}
        {updatedActivity?.activity_type == ACTIVITY_TYPES.REVIEW && (
          <Autocomplete
            sx={textFieldStyle}
            value={getSelectedActivity(updatedActivity.associated_data_entry_activity_template_id)}
            id="rep"
            options={allActivities
              .filter((it) => it.activity_type == ACTIVITY_TYPES.DATA_ENTRY)
              .map((it) => ({
                value: it.id,
                label: it?.activity_template_name,
              }))}
            size="small"
            renderInput={(params) => (
              <TextField {...params} label="Associated Data Entry Activity" />
            )}
            onChange={(e, v) => {
              setUpdatedActivity({
                ...updatedActivity,
                associated_data_entry_activity_template_id: v?.value,
              })
            }}
          />
        )}

        {/* Associated data entry activity template */}
        {updatedActivity?.activity_type == ACTIVITY_TYPES.PARALLEL_END && (
          <Autocomplete
            sx={textFieldStyle}
            value={getSelectedActivity(
              updatedActivity?.parallel_config?.associated_parallel_start_activity_template_id,
            )}
            id="rep"
            options={allActivities
              .filter((it) => it.activity_type == ACTIVITY_TYPES.PARALLEL_START)
              .map((it) => ({
                value: it.id,
                label: it?.activity_template_name,
              }))}
            size="small"
            renderInput={(params) => (
              <TextField {...params} label="Associated Parallel Start Activity" />
            )}
            onChange={(e, v) => {
              setUpdatedActivity({
                ...updatedActivity,
                parallel_config: {
                  ...updatedActivity?.parallel_config,
                  associated_parallel_start_activity_template_id: v?.value,
                },
              })
            }}
          />
        )}

        {/* Color Code Selection */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <TextField
            id="color"
            size="small"
            variant="outlined"
            color="primary"
            type="color"
            sx={{
              ...textFieldStyle,
              width: '20%',
              padding: '5px',
              height: '1.4375em',
            }}
            value={updatedActivity?.color_code}
            onChange={(e) => {
              setUpdatedActivity({
                ...updatedActivity,
                color_code: e.target.value,
              })
            }}
          />
          <TextField
            sx={textFieldStyle}
            id="color"
            size="small"
            variant="outlined"
            color="primary"
            style={{
              width: '80%',
              padding: '5px',
            }}
            value={updatedActivity?.color_code}
            onChange={(e) => {
              setUpdatedActivity({
                ...updatedActivity,
                color_code: e.target.value,
              })
            }}
            label={`Color Code`}
          />
        </div>

        {/* Estimated Hours to Complete*/}
        <TextField
          sx={textFieldStyle}
          type="number"
          value={updatedActivity?.expected_minutes_to_complete / 60 || 0}
          fullWidth
          size="small"
          variant="outlined"
          color="primary"
          onChange={(e) => {
            setUpdatedActivity({
              ...updatedActivity,
              expected_minutes_to_complete: e.target.value * 60 || 0,
            })
          }}
          label={`Estimated Hours to Complete`}
        />

        {/* Auto Close Settings - Toggle */}
        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Switch
              size="small"
              checked={autoCloseEnabled}
              onChange={(e) => setAutoCloseEnabled(e.target.checked)}
              color="primary"
            />
          }
          label={<Typography sx={{ fontSize: '12px' }}>Enable Auto Close</Typography>}
        />

        {autoCloseEnabled && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box display="flex" gap={1}>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                label="Duration"
                value={
                  updatedActivity?.time_in_min
                    ? updatedActivity.time_in_min >= 1440
                      ? updatedActivity.time_in_min / 1440
                      : updatedActivity.time_in_min >= 60
                        ? updatedActivity.time_in_min / 60
                        : updatedActivity.time_in_min
                    : ''
                }
                onChange={(e) => {
                  const val = e.target.value ? parseFloat(e.target.value) : 0

                  let unit = updatedActivity?.time_unit
                  if (!unit) {
                    if (updatedActivity?.time_in_min >= 1440) unit = 'days'
                    else if (updatedActivity?.time_in_min >= 60) unit = 'hours'
                    else unit = 'minutes'
                  }

                  let minutes = val
                  if (unit === 'hours') minutes = val * 60
                  if (unit === 'days') minutes = val * 1440

                  setUpdatedActivity({
                    ...updatedActivity,
                    time_in_min: minutes,
                    time_duration: val,
                    time_unit: unit,
                  })
                }}
                fullWidth
              />
              <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={
                    updatedActivity?.time_in_min >= 1440
                      ? 'days'
                      : updatedActivity?.time_in_min >= 60
                        ? 'hours'
                        : 'minutes'
                  }
                  onChange={(e) => {
                    const newUnit = e.target.value
                    const currentDuration =
                      updatedActivity?.time_duration ||
                      (updatedActivity?.time_in_min >= 1440
                        ? updatedActivity.time_in_min / 1440
                        : updatedActivity?.time_in_min >= 60
                          ? updatedActivity.time_in_min / 60
                          : updatedActivity?.time_in_min || 0)

                    let minutes = currentDuration
                    if (newUnit === 'hours') minutes = currentDuration * 60
                    if (newUnit === 'days') minutes = currentDuration * 1440

                    setUpdatedActivity({
                      ...updatedActivity,
                      time_in_min: minutes,
                      time_unit: newUnit,
                    })
                  }}
                  label="Unit"
                >
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="hours">Hours</MenuItem>
                  <MenuItem value="days">Days</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={updatedActivity?.is_working_days || false}
                  onChange={(e) =>
                    setUpdatedActivity({
                      ...updatedActivity,
                      is_working_days: e.target.checked,
                    })
                  }
                  color="primary"
                  size="small"
                />
              }
              label={<Typography sx={{ fontSize: '12px' }}>Working Days Only</Typography>}
            />

            <FormControl variant="outlined" size="small" color="primary" fullWidth>
              <InputLabel id="default-route-label">Default Route (Auto Close)</InputLabel>
              <Select
                labelId="default-route-label"
                value={updatedActivity?.default_route_template_id || ''}
                label="Default Route (Auto Close)"
                onChange={(e) => {
                  setUpdatedActivity({
                    ...updatedActivity,
                    default_route_template_id: e.target.value,
                  })
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {routes.map((route) => (
                  <MenuItem key={route.route_template_id} value={route.route_template_id}>
                    {route.route_template_name} ({route.route_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Is Scoped Review */}

        <FormControl variant="outlined" fullWidth size="small" color="primary">
          <InputLabel id="select-option-label">Is Scoped Review</InputLabel>
          <Select
            size="small"
            color="primary"
            labelId="select-option-label"
            id="select-option"
            value={updatedActivity?.is_scoped_review ? 'yes' : 'no'}
            onChange={(e) => {
              const updated = { ...updatedActivity, is_scoped_review: e.target.value == 'yes' }
              if (e.target.value != 'yes') {
                updated['scoped_review_field_template_ids'] = []
              }
              setUpdatedActivity(updated)
            }}
            label="is_scoped_review"
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>

        {/* scoped_review_field_template_ids */}
        {updatedActivity?.is_scoped_review ? (
          <FormControl variant="outlined" fullWidth size="small" color="primary">
            <Autocomplete
              id="select-derived-field"
              multiple
              options={ftData.map((item) => ({
                value: item.id,
                label: item.is_subfield
                  ? `${getRelatedTableName(item.table_field_template_id)} /${
                      item.field_template_name
                    }`
                  : item.field_template_name,
              }))}
              size="small"
              getOptionLabel={(item) => item.label}
              value={
                ftData
                  .filter((it) => {
                    const scopedReviewIds = Array.isArray(
                      updatedActivity?.scoped_review_field_template_ids,
                    )
                      ? updatedActivity.scoped_review_field_template_ids
                      : updatedActivity?.scoped_review_field_template_ids
                          ?.split(',')
                          .map((id) => id.trim())

                    return scopedReviewIds?.includes(it.id)
                  })
                  .map((item) => ({
                    value: item.id,
                    label: item.is_subfield
                      ? `${getRelatedTableName(item.table_field_template_id)}/${
                          item.field_template_name
                        }`
                      : item.field_template_name,
                  })) || []
              }
              onChange={(_, newValue) => {
                setUpdatedActivity({
                  ...updatedActivity,
                  scoped_review_field_template_ids: newValue.map((item) => item.value) || [],
                })
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Scoped Review Field Template Ids"
                  variant="outlined"
                  color="primary"
                />
              )}
            />
          </FormControl>
        ) : null}

        {/* Assigned Team */}
        <Autocomplete
          sx={textFieldStyle}
          size="small"
          multiple
          value={teams.filter((it) => updatedActivity.assigned_team_ids?.includes(it.id)) || null}
          options={[...teams].sort((a, b) => {
            if (a.team_type === 'internal') return -1
            if (b.team_type === 'internal') return 1
            return -b.team_type.localeCompare(a.team_type)
          })}
          groupBy={(option) => option.team_type}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => <TextField {...params} label="Select Assignment Teams" />}
          renderGroup={(params) => {
            return (
              <li key={params.key}>
                <GroupHeader>{params.group}</GroupHeader>
                <GroupItems>{params.children}</GroupItems>
              </li>
            )
          }}
          onChange={(e, value) => {
            setUpdatedActivity({
              ...updatedActivity,
              assigned_team_ids: value.map((it) => it.id),
              is_assigned_to_initiator: false,
              is_dynamic_assignment: false,
              is_reporter_assignment: false,
              reportee_activity_template_id: null,
            })
          }}
        />

        {/* Assign to Initiator */}
        <FormControl variant="outlined" fullWidth size="small" color="primary">
          <InputLabel id="select-option-label">Assign to Initiator</InputLabel>
          <Select
            size="small"
            color="primary"
            labelId="select-option-label"
            id="select-option"
            value={updatedActivity?.is_assigned_to_initiator ? 'yes' : 'no'}
            onChange={(e) => {
              setUpdatedActivity({
                ...updatedActivity,
                is_assigned_to_initiator: e.target.value == 'yes',
                is_dynamic_assignment: false,
                is_reporter_assignment: false,
                reportee_activity_template_id: null,
                assigned_team_ids: [],
              })
            }}
            label="Assign to Initiator"
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>

        {/*Can Be Delegated?*/}
        <FormControl variant="outlined" fullWidth size="small" color="primary">
          <InputLabel id="select-option-label">Can Be Delegated?</InputLabel>
          <Select
            size="small"
            color="primary"
            labelId="select-option-label"
            id="select-option"
            value={updatedActivity?.is_delegatable ? 'yes' : 'no'}
            onChange={(e) => {
              const updated = {
                ...updatedActivity,
                is_delegatable: e.target.value == 'yes',
              }
              if (e.target.value != 'yes') {
                updated['delegation_team_ids'] = []
              }
              setUpdatedActivity(updated)
            }}
            label="Can Be Delegated?"
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>

        {updatedActivity?.is_delegatable && (
          <Autocomplete
            multiple
            size="small"
            value={teams.filter((it) => updatedActivity?.delegation_team_ids.includes(it.id)) || []}
            options={teams.sort((a, b) => {
              if (a.team_type === 'internal') return -1
              if (b.team_type === 'internal') return 1
              return -b.team_type.localeCompare(a.team_type)
            })}
            groupBy={(option) => option.team_type}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => <TextField {...params} label="Select Delegation Teams" />}
            renderGroup={(params) => {
              return (
                <li key={params.key}>
                  <GroupHeader>{params.group}</GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              )
            }}
            onChange={(e, v) => {
              setUpdatedActivity({
                ...updatedActivity,
                delegation_team_ids: v?.map((it) => it.id) || [],
              })
            }}
          />
        )}

        {/*Dynamic Assignment */}
        <FormControl variant="outlined" fullWidth size="small" color="primary">
          <InputLabel id="select-option-label">Dynamic Assignment</InputLabel>
          <Select
            size="small"
            color="primary"
            labelId="select-option-label"
            id="select-option"
            value={updatedActivity?.is_dynamic_assignment ? 'yes' : 'no'}
            onChange={(e) => {
              setUpdatedActivity({
                ...updatedActivity,
                is_dynamic_assignment: e.target.value == 'yes',
                is_reporter_assignment: false,
                reportee_activity_template_id: null,
                is_assigned_to_initiator: false,
                assigned_team_ids: [],
              })
            }}
            label="Dynamic Assignment"
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>

        {/* derived from fields */}
        {updatedActivity?.is_dynamic_assignment ? (
          <FormControl variant="outlined" fullWidth size="small" color="primary">
            <Autocomplete
              id="select-derived-field"
              options={ftData
                ?.filter((it) => it.data_structure == FIELD_TYPES.LIST && it.list_id == null)
                .sort((a, b) => -b.activity_template_name.localeCompare(a.activity_template_name))}
              groupBy={(option) => option?.activity_template_name}
              size="small"
              getOptionLabel={(item) => item?.field_template_name}
              value={selectedAssignmentField}
              onChange={(_, newValue) => setSelectedAssignmentField(newValue)}
              renderGroup={(params) => (
                <li key={params.key}>
                  <GroupHeader>{params.group}</GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select derived field"
                  color="primary"
                />
              )}
            />
          </FormControl>
        ) : null}

        {/*Assign To Reporter */}
        <FormControl variant="outlined" fullWidth size="small" color="primary">
          <InputLabel id="select-option-label">Assign To Reporter</InputLabel>
          <Select
            size="small"
            color="primary"
            labelId="select-option-label"
            id="select-option"
            value={updatedActivity?.is_reporter_assignment ? 'yes' : 'no'}
            onChange={(e) => {
              const updated = {
                ...updatedActivity,
                is_reporter_assignment: e.target.value == 'yes',
                is_hidden_in_process_flow_view: false,
                is_assigned_to_initiator: false,
                is_dynamic_assignment: false,
                assigned_team_ids: [],
              }

              if (e.target.value == 'no') {
                updated['reportee_activity_template_id'] = null
              }

              setUpdatedActivity(updated)
            }}
            label="Assigned To Reporter"
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>

        {/*Hide Activity in Processflowviewer */}
        <FormControl variant="outlined" fullWidth size="small" color="primary">
          <InputLabel id="select-option-label">Is Hidden in Process Flow Viewer</InputLabel>
          <Select
            size="small"
            color="primary"
            labelId="select-option-label"
            id="select-option"
            value={updatedActivity?.is_hidden_in_process_flow_view ? 'yes' : 'no'}
            onChange={(e) => {
              const updated = {
                ...updatedActivity,
                is_hidden_in_process_flow_view: e.target.value == 'yes',
              }
              setUpdatedActivity(updated)
            }}
            label="Is Hidden in Process Flow Viewer"
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>

        {/*Hide Overall Comments for specific activities */}
        <FormControl variant="outlined" fullWidth size="small" color="primary">
          <InputLabel id="select-option-label">Is Overall Comments Hidden</InputLabel>
          <Select
            size="small"
            color="primary"
            labelId="select-option-label"
            id="select-option"
            value={updatedActivity?.hide_overall_comment ? 'yes' : 'no'}
            onChange={(e) => {
              const updated = {
                ...updatedActivity,
                hide_overall_comment: e.target.value == 'yes',
              }
              setUpdatedActivity(updated)
            }}
            label="Is Overall Comments Hidden"
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>

        {/* Assigned Teams for Queries */}
        <Autocomplete
          multiple
          size="small"
          options={teams}
          getOptionLabel={(option) => option.name}
          value={teams.filter((team) =>
            (updatedActivity?.query_assigned_team_ids || []).includes(team.id),
          )}
          onChange={(event, newValue) => {
            setUpdatedActivity({
              ...updatedActivity,
              query_assigned_team_ids: newValue.map((item) => item.id),
            })
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Assigned Teams for Queries"
              placeholder="Search teams..."
            />
          )}
        />

        {updatedActivity?.is_reporter_assignment ? (
          <Autocomplete
            sx={textFieldStyle}
            value={getSelectedActivity(updatedActivity.reportee_activity_template_id)}
            id="rep"
            options={allActivities.map((it) => ({
              value: it.id,
              label: it.activity_template_name,
            }))}
            size="small"
            renderInput={(params) => <TextField {...params} label="Activity" />}
            onChange={(e, v) => {
              setUpdatedActivity({
                ...updatedActivity,
                reportee_activity_template_id: v?.value,
              })
            }}
          />
        ) : (
          <></>
        )}

        <div
          style={{
            display: 'flex',
            columnGap: 16,
            width: '100%',
            justifyContent: 'flex-end',
          }}
        >
          <Button variant="contained" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setDialogOpen(false)
              deleteActivity(activityTemplate)
            }}
          >
            Delete
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              saveActivityDetails({
                ...updatedActivity,
                dynamic_assignment_field_template_id: selectedAssignmentField?.id,
                ...(autoCloseEnabled
                  ? {}
                  : {
                      time_in_min: 0,
                      is_working_days: false,
                      default_route_template_id: null,
                    }),
              })
            }}
          >
            Save Changes
          </Button>
        </div>
      </Box>
    </DialogWrapper>
  )
}

export default UpdateActivityDetails
